import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Home from '@/pages/Home';
import Catalog from '@/pages/Catalog';
import ContestDetail from '@/pages/ContestDetail';
import AuthPage from '@/pages/AuthPage';
import EmailVerification from '@/pages/EmailVerification';
import Library from '@/pages/Library';
import Reader from '@/pages/Reader';
import QuizPage from '@/pages/QuizPage';
import PaymentResult from '@/pages/PaymentResult';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminContests from '@/pages/admin/AdminContests';
import AdminContent from '@/pages/admin/AdminContent';
import AdminQuiz from '@/pages/admin/AdminQuiz';
import AdminUsersOrders from '@/pages/admin/AdminUsersOrders';
import AdminBando from '@/pages/admin/AdminBando';
import AdminSales from '@/pages/admin/AdminSales';
import AdminLearningGraph from '@/pages/admin/AdminLearningGraph';
import AdminPipeline from '@/pages/admin/AdminPipeline';
import AdminCoveragePlanner from '@/pages/admin/AdminCoveragePlanner';
import Account from '@/pages/Account';
import { ForgotPassword, ResetPassword } from '@/pages/PasswordReset';
import { adminDecision, authDecision, demoRouteDecision } from '@/lib/guards';
import PartnerProgram from '@/pages/PartnerProgram';
import AffiliateTerms from '@/pages/AffiliateTerms';
import AdminAffiliates from '@/pages/admin/AdminAffiliates';
import AdminAdmRevision from '@/pages/admin/AdminAdmRevision';
import AdminInps499Import from '@/pages/admin/AdminInps499Import';
import AffiliateTracking from '@/components/AffiliateTracking';
import AffiliateEmailVerification from '@/pages/AffiliateEmailVerification';

const Spinner = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
  </div>
);

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const decision = authDecision({ user, loading });
  if (decision === 'loading') return <Spinner />;
  if (decision === 'login') return <Navigate to={`/accedi?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const decision = adminDecision({ user, loading });
  if (decision === 'loading') return <Spinner />;
  if (decision === 'login') return <Navigate to="/accedi" replace />;
  if (decision === 'home') return <Navigate to="/" replace />;
  return children;
}

export function ExcludePartnerDemo({ children }) {
  const { user, loading } = useAuth();
  const decision = demoRouteDecision({ user, loading });
  if (decision === 'loading') return <Spinner />;
  if (decision === 'library') return <Navigate to="/area-personale" replace />;
  return children;
}

function AppRouter() {
  const affiliateEnabled = process.env.REACT_APP_AFFILIATE_PROGRAM_ENABLED === 'true';
  return (
    <Routes>
      <Route path="/" element={<ExcludePartnerDemo><Navbar /><Home /><Footer /></ExcludePartnerDemo>} />
      <Route path="/concorsi" element={<ExcludePartnerDemo><Navbar /><Catalog /><Footer /></ExcludePartnerDemo>} />
      <Route path="/concorsi/:contestId" element={<ExcludePartnerDemo><Navbar /><ContestDetail /><Footer /></ExcludePartnerDemo>} />
      <Route path="/accedi" element={<><Navbar /><AuthPage /><Footer /></>} />
      <Route path="/verifica-email" element={<><Navbar /><EmailVerification /><Footer /></>} />
      <Route path="/recupera-password" element={<><Navbar /><ForgotPassword /><Footer /></>} />
      <Route path="/reset-password" element={<><Navbar /><ResetPassword /><Footer /></>} />
      <Route path="/programma-partner" element={affiliateEnabled ? <><Navbar /><PartnerProgram /><Footer /></> : <Navigate to="/" replace />} />
      <Route path="/programma-partner/verifica-email" element={affiliateEnabled ? <><Navbar /><AffiliateEmailVerification /><Footer /></> : <Navigate to="/" replace />} />
      <Route path="/termini-programma-partner" element={affiliateEnabled ? <><Navbar /><AffiliateTerms /><Footer /></> : <Navigate to="/" replace />} />
      <Route path="/account" element={<RequireAuth><ExcludePartnerDemo><Navbar /><Account /><Footer /></ExcludePartnerDemo></RequireAuth>} />
      <Route path="/pagamento/successo" element={<ExcludePartnerDemo><Navbar /><PaymentResult /><Footer /></ExcludePartnerDemo>} />
      <Route path="/pagamento/annullato" element={<ExcludePartnerDemo><Navbar /><PaymentResult /><Footer /></ExcludePartnerDemo>} />
      <Route path="/area-personale" element={<RequireAuth><Navbar /><Library /><Footer /></RequireAuth>} />
      <Route path="/reader/:contestId" element={<RequireAuth><Navbar /><Reader /></RequireAuth>} />
      <Route path="/reader/:contestId/:sectionId" element={<RequireAuth><Navbar /><Reader /></RequireAuth>} />
      <Route path="/quiz/:contestId" element={<RequireAuth><Navbar /><QuizPage /><Footer /></RequireAuth>} />
      <Route path="/admin/preview/concorsi/:contestId" element={<RequireAdmin><Navbar /><ContestDetail preview /><Footer /></RequireAdmin>} />
      <Route path="/admin/preview/reader/:contestId" element={<RequireAdmin><Navbar /><Reader preview /></RequireAdmin>} />
      <Route path="/admin/preview/reader/:contestId/:sectionId" element={<RequireAdmin><Navbar /><Reader preview /></RequireAdmin>} />
      <Route path="/admin/preview/quiz/:contestId" element={<RequireAdmin><Navbar /><QuizPage preview /><Footer /></RequireAdmin>} />
      <Route path="/admin" element={<RequireAdmin><Navbar /><AdminLayout /></RequireAdmin>}>
        <Route index element={<AdminDashboard />} />
        <Route path="concorsi" element={<AdminContests />} />
        <Route path="contenuti" element={<AdminContent />} />
        <Route path="learning-graph" element={<AdminLearningGraph />} />
        <Route path="pipeline" element={<AdminPipeline />} />
        <Route path="coverage-planner" element={<AdminCoveragePlanner />} />
        <Route path="quiz" element={<AdminQuiz />} />
        <Route path="utenti" element={<AdminUsersOrders />} />
        <Route path="bando" element={<AdminBando />} />
        <Route path="adm-revisione" element={<AdminAdmRevision />} />
        <Route path="inps499-importazione" element={<AdminInps499Import />} />
        <Route path="vendite" element={<AdminSales />} />
        <Route path="affiliati" element={<AdminAffiliates />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <AffiliateTracking />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
